var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Paper, Stack, TextField, Typography, } from "@mui/material";
import React from "react";
import { useFormContext } from "react-hook-form";
var DAY_LABELS = [
    { key: "monday", label: "Monday", short: "Mon" },
    { key: "tuesday", label: "Tuesday", short: "Tue" },
    { key: "wednesday", label: "Wednesday", short: "Wed" },
    { key: "thursday", label: "Thursday", short: "Thu" },
    { key: "friday", label: "Friday", short: "Fri" },
    { key: "saturday", label: "Saturday", short: "Sat" },
    { key: "sunday", label: "Sunday", short: "Sun" },
];
var DEFAULT_DAY_CONFIG = {
    enabled: false,
    timeWindows: [{ startTime: "09:00", endTime: "17:00" }],
};
export var createDefaultDays = function () { return ({
    monday: __assign({}, DEFAULT_DAY_CONFIG),
    tuesday: __assign({}, DEFAULT_DAY_CONFIG),
    wednesday: __assign({}, DEFAULT_DAY_CONFIG),
    thursday: __assign({}, DEFAULT_DAY_CONFIG),
    friday: __assign({}, DEFAULT_DAY_CONFIG),
    saturday: __assign({}, DEFAULT_DAY_CONFIG),
    sunday: __assign({}, DEFAULT_DAY_CONFIG),
}); };
// Calculate slots per window based on match timing
var calculateSlotsPerWindow = function (window, slotDuration) {
    var _a, _b, _c, _d;
    if (!window)
        return 0;
    var startParts = window.startTime.split(":").map(Number);
    var endParts = window.endTime.split(":").map(Number);
    var startH = (_a = startParts[0]) !== null && _a !== void 0 ? _a : 9;
    var startM = (_b = startParts[1]) !== null && _b !== void 0 ? _b : 0;
    var endH = (_c = endParts[0]) !== null && _c !== void 0 ? _c : 17;
    var endM = (_d = endParts[1]) !== null && _d !== void 0 ? _d : 0;
    var totalMinutes = endH * 60 + endM - (startH * 60 + startM);
    if (totalMinutes <= 0)
        return 0; // End time must be after start time
    return Math.floor(totalMinutes / slotDuration);
};
// Check if a time window is valid (end after start)
var isTimeWindowValid = function (window) {
    var _a, _b, _c, _d;
    if (!window)
        return true;
    var startParts = window.startTime.split(":").map(Number);
    var endParts = window.endTime.split(":").map(Number);
    var startMinutes = ((_a = startParts[0]) !== null && _a !== void 0 ? _a : 0) * 60 + ((_b = startParts[1]) !== null && _b !== void 0 ? _b : 0);
    var endMinutes = ((_c = endParts[0]) !== null && _c !== void 0 ? _c : 0) * 60 + ((_d = endParts[1]) !== null && _d !== void 0 ? _d : 0);
    return endMinutes > startMinutes;
};
// Calculate total slots per week for a pitch
var calculatePitchSlotsPerWeek = function (schedule, slotDuration) {
    return DAY_LABELS.reduce(function (sum, d) {
        var dayConfig = schedule.days[d.key];
        if (!dayConfig.enabled || dayConfig.timeWindows.length === 0)
            return sum;
        return (sum + calculateSlotsPerWindow(dayConfig.timeWindows[0], slotDuration));
    }, 0);
};
export var ScheduleWindowStep = function (_a) {
    var context = _a.context;
    var pitches = context.pitches, gamesPerRound = context.gamesPerRound, totalRounds = context.totalRounds;
    var _b = useFormContext(), watch = _b.watch, setValue = _b.setValue;
    var matchTiming = watch("matchTiming");
    var scheduleWindowValue = watch("scheduleWindow");
    var defaultSlotDuration = matchTiming
        ? matchTiming.matchDuration +
            matchTiming.halfTimeBreak +
            matchTiming.warmupTime
        : 45;
    // Build pitchSchedules from form state, syncing with current pitches
    var pitchSchedules = React.useMemo(function () {
        var _a, _b;
        var existingSchedules = (_a = scheduleWindowValue === null || scheduleWindowValue === void 0 ? void 0 : scheduleWindowValue.pitchSchedules) !== null && _a !== void 0 ? _a : [];
        var existingMap = new Map(existingSchedules.map(function (s) { return [s.pitchId, s]; }));
        if (pitches.length === 0) {
            // No pitches - use "All Venues" mode
            var existing = existingMap.get(0);
            return [
                {
                    pitchId: 0,
                    pitchName: "All Venues",
                    venueId: 0,
                    venueName: "",
                    days: (_b = existing === null || existing === void 0 ? void 0 : existing.days) !== null && _b !== void 0 ? _b : createDefaultDays(),
                },
            ];
        }
        // Map pitches to schedules, preserving existing day configs
        return pitches.map(function (p) {
            var _a;
            var existing = existingMap.get(p.id);
            return {
                pitchId: p.id,
                pitchName: p.name,
                venueId: p.venueId,
                venueName: p.venueName,
                days: (_a = existing === null || existing === void 0 ? void 0 : existing.days) !== null && _a !== void 0 ? _a : createDefaultDays(),
            };
        });
    }, [scheduleWindowValue === null || scheduleWindowValue === void 0 ? void 0 : scheduleWindowValue.pitchSchedules, pitches]);
    // Helper to update schedules via form context
    var updateSchedules = React.useCallback(function (newSchedules) {
        setValue("scheduleWindow", { pitchSchedules: newSchedules });
    }, [setValue]);
    var handleDayToggle = function (pitchIndex, dayKey) {
        var _a;
        var schedule = pitchSchedules[pitchIndex];
        if (!schedule)
            return;
        var current = schedule.days[dayKey];
        var updated = __spreadArray([], __read(pitchSchedules), false);
        updated[pitchIndex] = __assign(__assign({}, schedule), { days: __assign(__assign({}, schedule.days), (_a = {}, _a[dayKey] = __assign(__assign({}, current), { enabled: !current.enabled, timeWindows: !current.enabled
                    ? [{ startTime: "09:00", endTime: "17:00" }]
                    : current.timeWindows }), _a)) });
        updateSchedules(updated);
    };
    var handleTimeChange = function (pitchIndex, dayKey, field, newValue) {
        var _a;
        var schedule = pitchSchedules[pitchIndex];
        if (!schedule)
            return;
        var current = schedule.days[dayKey];
        var existingWindow = current.timeWindows[0] || {
            startTime: "09:00",
            endTime: "17:00",
        };
        var updated = __spreadArray([], __read(pitchSchedules), false);
        updated[pitchIndex] = __assign(__assign({}, schedule), { days: __assign(__assign({}, schedule.days), (_a = {}, _a[dayKey] = __assign(__assign({}, current), { timeWindows: [
                    {
                        startTime: field === "startTime" ? newValue : existingWindow.startTime,
                        endTime: field === "endTime" ? newValue : existingWindow.endTime,
                    },
                ] }), _a)) });
        updateSchedules(updated);
    };
    var totalSlotsPerWeek = pitchSchedules.reduce(function (sum, schedule) {
        return sum + calculatePitchSlotsPerWeek(schedule, defaultSlotDuration);
    }, 0);
    var totalGamesNeeded = gamesPerRound * totalRounds;
    var estimatedWeeksNeeded = totalSlotsPerWeek > 0 ? Math.ceil(totalGamesNeeded / totalSlotsPerWeek) : 0;
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "Define which days and times matches can be scheduled at each pitch/venue." }), gamesPerRound > 0 && totalSlotsPerWeek > 0 && (_jsxs(Alert, { severity: "info", sx: { mb: 3 }, children: ["Estimated ", estimatedWeeksNeeded, " weeks to complete ", totalRounds, " ", "rounds (", totalGamesNeeded, " games) at ", totalSlotsPerWeek, " slots/week"] })), _jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "subtitle2", sx: { mb: 2 }, children: "Pitch Availability" }), pitchSchedules.map(function (schedule, pitchIndex) {
                        var enabledDays = DAY_LABELS.filter(function (d) { return schedule.days[d.key].enabled; });
                        var slotsPerWeek = calculatePitchSlotsPerWeek(schedule, defaultSlotDuration);
                        return (_jsxs(Accordion, { defaultExpanded: true, sx: { mb: 1 }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: _jsxs(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            pr: 2,
                                        }, children: [_jsx(Box, { children: _jsx(Typography, { variant: "subtitle2", children: schedule.venueName
                                                        ? "".concat(schedule.venueName, " \u2192 ").concat(schedule.pitchName)
                                                        : schedule.pitchName }) }), _jsx(Chip, { size: "small", label: "".concat(slotsPerWeek, " slots/week"), color: slotsPerWeek > 0 ? "primary" : "default", variant: "outlined" })] }) }), _jsxs(AccordionDetails, { children: [_jsx(Box, { sx: { mb: 2 }, children: _jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Slot Duration: ", _jsxs("strong", { children: [defaultSlotDuration, " min"] }), " ", "(from match timing)"] }) }), _jsx(Stack, { direction: "row", spacing: 1, sx: { mb: 2, flexWrap: "wrap", gap: 1 }, children: DAY_LABELS.map(function (day) { return (_jsx(Chip, { label: day.short, color: schedule.days[day.key].enabled ? "primary" : "default", variant: schedule.days[day.key].enabled ? "filled" : "outlined", onClick: function () { return handleDayToggle(pitchIndex, day.key); }, size: "small" }, day.key)); }) }), enabledDays.length > 0 && (_jsx(Stack, { spacing: 1, children: enabledDays.map(function (day) {
                                                var timeWindow = schedule.days[day.key].timeWindows[0];
                                                var isValid = isTimeWindowValid(timeWindow);
                                                return (_jsxs(Box, { sx: {
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        flexWrap: "wrap",
                                                    }, children: [_jsx(Typography, { variant: "body2", sx: { minWidth: 70, fontWeight: 500 }, children: day.short }), _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(TextField, { type: "time", size: "small", value: (timeWindow === null || timeWindow === void 0 ? void 0 : timeWindow.startTime) || "09:00", onChange: function (e) {
                                                                        return handleTimeChange(pitchIndex, day.key, "startTime", e.target.value);
                                                                    }, error: !isValid, sx: { width: 140 }, inputProps: { step: 900 } }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u2013" }), _jsx(TextField, { type: "time", size: "small", value: (timeWindow === null || timeWindow === void 0 ? void 0 : timeWindow.endTime) || "17:00", onChange: function (e) {
                                                                        return handleTimeChange(pitchIndex, day.key, "endTime", e.target.value);
                                                                    }, error: !isValid, sx: { width: 140 }, inputProps: { step: 900 } }), isValid ? (_jsx(Chip, { size: "small", label: "".concat(calculateSlotsPerWindow(timeWindow, defaultSlotDuration)), variant: "outlined", sx: { minWidth: 30 } })) : (_jsx(Typography, { variant: "caption", color: "error", sx: { whiteSpace: "nowrap" }, children: "End must be after start" }))] })] }, day.key));
                                            }) })), enabledDays.length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Select days when this pitch is available" }))] })] }, schedule.pitchId));
                    })] }), _jsxs(Paper, { variant: "outlined", sx: {
                    p: 3,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 2, textTransform: "uppercase", letterSpacing: 0.5 }, children: "Schedule Summary" }), _jsxs(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                            gap: 2,
                        }, children: [_jsxs(Box, { children: [_jsx(Stack, { direction: "row", spacing: 1, alignItems: "center", children: _jsx(Typography, { variant: "h4", color: "primary.main", children: pitchSchedules.length }) }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: pitchSchedules.length === 1 ? "pitch" : "pitches" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: totalSlotsPerWeek }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "slots/week total" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: gamesPerRound }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "games/round" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: totalGamesNeeded }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "total games" })] })] }), gamesPerRound > 0 && totalSlotsPerWeek > 0 && (_jsx(_Fragment, { children: totalSlotsPerWeek >= gamesPerRound ? (_jsx(Alert, { severity: "success", icon: _jsx(InfoOutlinedIcon, {}), sx: { mt: 2 }, children: _jsxs(Typography, { variant: "body2", children: ["You can schedule ", _jsx("strong", { children: "1 round per week" }), " with", " ", totalSlotsPerWeek - gamesPerRound, " spare slots."] }) })) : (_jsx(Alert, { severity: "warning", icon: _jsx(ErrorOutlineIcon, {}), sx: { mt: 2 }, children: _jsxs(Typography, { variant: "body2", children: ["You need ", _jsxs("strong", { children: [gamesPerRound, " slots/week"] }), " for 1 round but only have ", totalSlotsPerWeek, ". Consider adding more availability or playing multiple rounds per week may take longer."] }) })) })), totalSlotsPerWeek === 0 && (_jsx(Alert, { severity: "warning", sx: { mt: 2 }, children: _jsx(Typography, { variant: "body2", children: "Select at least one day on at least one pitch to schedule matches." }) }))] })] }));
};
